import { normalizeUsdaRecallRecords } from './useRecallsApi/utils';

import type {
  CpscRecallParams,
  CpscRecallRecord,
  FdaEnforcementParams,
  FdaEnforcementResponse,
  UsdaRecallParams,
  UsdaRecallRecord,
  VehicleRecallRecord,
  VehicleRecallsParams,
  VehicleRecallsResponse,
} from './interfaces';

const RECALLS_FETCH_OPTIONS: RequestInit = {
  method: 'GET',
  credentials: 'omit',
  headers: { Accept: 'application/json' },
};

async function fetchRecallsJson<T>(url: string): Promise<T> {
  const response = await fetch(url, RECALLS_FETCH_OPTIONS);
  if (!response.ok) {
    throw new Error(
      `Recalls API error: ${response.status} ${response.statusText}`
    );
  }
  const data = (await response.json()) as T;
  return data;
}

const appendNonEmptyParams = (
  url: URL,
  params: Record<string, string | undefined | null>
): void => {
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });
};

// ===================== FDA openFDA Enforcement =====================

const FDA_BASE_URL = 'https://api.fda.gov/';

const FDA_ENDPOINTS: Record<'drug' | 'device' | 'food', string> = {
  drug: 'drug/enforcement.json',
  device: 'device/enforcement.json',
  food: 'food/enforcement.json',
};

const fetchEnforcement = async (
  params: FdaEnforcementParams
): Promise<FdaEnforcementResponse> => {
  const { domain, searchParams } = params;
  const path = FDA_ENDPOINTS[domain];
  const url = new URL(path, FDA_BASE_URL);
  const limit = searchParams?.limit ?? 10;
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('sort', searchParams?.sort ?? 'report_date:desc');

  if (searchParams) {
    if (
      searchParams.search !== undefined &&
      searchParams.search !== null &&
      searchParams.search !== ''
    ) {
      url.searchParams.set('search', searchParams.search);
    }
    if (searchParams.skip !== undefined && searchParams.skip !== null) {
      url.searchParams.set('skip', String(searchParams.skip));
    }
  }

  return fetchRecallsJson<FdaEnforcementResponse>(url.toString());
};

export const fdaRecalls = {
  fetchEnforcement,
};

// ===================== USDA FSIS Recall =====================

const USDA_BASE_URL = 'https://www.fsis.usda.gov/';
const USDA_RECALL_PATH = 'fsis/api/recall/v/1';

const fetchUsdaRecalls = async (
  params?: UsdaRecallParams
): Promise<UsdaRecallRecord[]> => {
  const url = new URL(USDA_RECALL_PATH, USDA_BASE_URL);
  if (params && Object.keys(params).length > 0) {
    appendNonEmptyParams(url, params);
  }
  const records = await fetchRecallsJson<UsdaRecallRecord[]>(url.toString());
  return normalizeUsdaRecallRecords(records);
};

export const usdaRecalls = {
  fetchRecalls: fetchUsdaRecalls,
};

// ===================== CPSC Recall Retrieval =====================

const CPSC_RECALL_URL = 'https://www.saferproducts.gov/RestWebServices/Recall';

const fetchCpscRecalls = async (
  params?: CpscRecallParams
): Promise<CpscRecallRecord[]> => {
  const url = new URL(CPSC_RECALL_URL);
  url.searchParams.set('format', 'json');
  if (params && Object.keys(params).length > 0) {
    appendNonEmptyParams(url, params);
  }
  return fetchRecallsJson<CpscRecallRecord[]>(url.toString());
};

export const cpscRecalls = {
  fetchRecalls: fetchCpscRecalls,
};

// ===================== Vehicle Recalls =====================
const RECALLS_DD_PATH = '/hk/dd/v2/recalls';

interface VehicleRecallRaw {
  campaign?: string | null;
  components?: string | null;
  consequence?: string | null;
  date?: {
    full?: string;
    parsed?: { year?: number; month?: number; day?: number };
  } | null;
  remedy?: string | null;
  summary?: string | null;
  notes?: string | null;
  oem_description?: string | null;
  oem_recall_number?: string | null;
  status?: string | null;
  type?: string | null;
  [key: string]: unknown;
}

interface VehicleRecallsResponseRaw {
  complaints?: unknown[];
  recalls?: VehicleRecallRaw[];
  selections?: unknown;
  states_contact?: unknown[];
  automobile?: { vin?: string | null };
  stats?: unknown;
}

function normalizeVehicleRecallsResponse(
  raw: VehicleRecallsResponseRaw
): VehicleRecallsResponse {
  const recalls: VehicleRecallRecord[] = (raw.recalls ?? []).map((r) => ({
    campaign: r.campaign ?? undefined,
    components: r.components ?? undefined,
    consequence: r.consequence ?? undefined,
    date:
      typeof r.date === 'object' && r.date?.full !== null
        ? r?.date?.full
        : undefined,
    remedy: r.remedy ?? undefined,
    summary: r.summary ?? undefined,
    notes: r.notes ?? undefined,
    oem_description: r.oem_description ?? undefined,
    oem_recall_number: r.oem_recall_number ?? undefined,
    status: r.status ?? undefined,
    type: r.type ?? undefined,
  }));
  return {
    complaints: raw.complaints,
    recalls,
    selections: raw.selections,
    states_contact: raw.states_contact,
    automobile: raw.automobile
      ? { vin: raw.automobile.vin ?? undefined }
      : undefined,
    stats: raw.stats,
  };
}

function buildRelativeUrl(
  path: string,
  params: Record<string, string | undefined | null>
): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value));
    }
  });
  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
}

const fetchVehicleRecalls = async (
  params: VehicleRecallsParams
): Promise<VehicleRecallsResponse> => {
  const requestUrl = buildRelativeUrl(
    RECALLS_DD_PATH,
    params as Record<string, string | undefined | null>
  );
  const response = await fetch(requestUrl);
  if (!response.ok) {
    throw new Error(
      `Recalls API error: ${response.status} ${response.statusText}`
    );
  }
  const raw = (await response.json()) as VehicleRecallsResponseRaw;
  return normalizeVehicleRecallsResponse(raw);
};

export const vehicleRecalls = {
  fetchRecalls: fetchVehicleRecalls,
};
